<template>
    <div class="laskuri-block">
        <h2>{{ "App.Ajoneuvoverolaskuri.Header" | translate }}</h2>

        <div>
            <p>
                <a :href="$t('App.Ajoneuvoverolaskuri.ExtraInfoUrl')" target="_blank" rel="noopener noreferrer">
                    {{ "App.Ajoneuvoverolaskuri.ExtraInfoDescription" | translate }}
                </a>
            </p>
        </div>

        <div class="input-row">
            <label class="input-label full-width" for="vehicleSelect">{{ "App.Ajoneuvoverolaskuri.VehicleTypeLabel" | translate }}</label>
            <div class="dropdown full-width">
                <select id="vehicleSelect" v-model="selectedVehicle" @change="updateSelections">
                    <option v-for="(type, index) in vehicleTypes" :key="index" :value="type">
                        {{ $t(type) }}
                    </option>
                </select>
            </div>
        </div>

        <div class="input-row">
            <label class="input-label full-width" for="maximumVehicleMassInput">{{ "App.Ajoneuvoverolaskuri.MaximumVehicleMassLabel" | translate }}</label>
            <input id="maximumVehicleMassInput" class="default full-width" type="text" v-model="maximumVehicleMass" v-on:input="updateSelections" />
            <span class="invalid-input-message" v-if="invalidFields.maximumVehicleMass">{{ invalidFields.maximumVehicleMass }}</span>
        </div>

        <div v-if="!showFuelAndCOMeasure">
            <div class="input-row">
                <date-picker :uniqueId="'manufactureDate'"
                             :label="manufactureDateLabel"
                             v-on:newDate="changeDate">
                </date-picker>
            </div>

            <div class="input-row">
                <label class="input-label full-width" for="fuelSelect">{{ "App.Ajoneuvoverolaskuri.FuelTypeLabel" | translate }}</label>
                <div class="dropdown full-width">
                    <select id="fuelSelect" v-model="selectedFuel" @change="updateSelections">
                        <option v-for="(type, index) in fuelTypes" :key="index" :value="type">
                            {{ $t(type) }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="input-row">
                <label class="input-label full-width" for="co2measureSelect">{{ "App.Ajoneuvoverolaskuri.CO2MeasureTypeLable" | translate }}</label>
                <div class="dropdown full-width">
                    <select id="co2measureSelect" v-model="selectedCo2measure" @change="updateSelections">
                        <option v-for="(type, index) in co2measureTypes" :key="index" :value="type">
                            {{ $t(type) }}
                        </option>
                    </select>
                </div>
            </div>
            <div v-if="showCoValue">
                <div class="input-row">
                    <label class="input-label full-width" for="co2MeasureValueInput">{{ "App.Ajoneuvoverolaskuri.CO2MeasureLabel" | translate }}</label>
                    <input id="co2MeasureValueInput" class="default full-width" type="text" v-model="co2MeasureValue" v-on:input="updateSelections" />
                    <span class="invalid-input-message" v-if="invalidFields.co2MeasureValue">{{ invalidFields.co2MeasureValue }}</span>
                </div>
            </div>
        </div>

        <div v-if="showFuelAndCOMeasure">
            <div class="input-row">
                <label class="input-label full-width" for="axelAmountSelect">{{ "App.Ajoneuvoverolaskuri.AxelAmountsLabel" | translate }}</label>
                <div class="dropdown full-width">
                    <select id="axelAmountSelect" v-model="selectedAxelAmount" @change="updateSelections">
                        <option v-for="(type, index) in axelAmounts" :key="index" :value="type">
                            {{ $t(type) }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="input-row">
                <label class="input-label full-width" for="trailerTypeSelect">{{ "App.Ajoneuvoverolaskuri.TrailerTypesLabel" | translate }}</label>
                <div class="dropdown full-width">
                    <select id="trailerTypeSelect" v-model="selectedTrailerType" @change="updateSelections">
                        <option v-for="(type, index) in trailerTypes" :key="index" :value="type">
                            {{ $t(type) }}
                        </option>
                    </select>
                </div>
            </div>
        </div>

        <h2>{{ "App.Ajoneuvoverolaskuri.ResultsHeader" | translate }}</h2>
        <p id="fillInfoP" v-if="showMaxWeightInfo == null">{{ "App.Ajoneuvoverolaskuri.FillFieldsInfoLabel" | translate }}</p>
        <p id="calculationTypeWeightP" v-if="showMaxWeightInfo">{{ "App.Ajoneuvoverolaskuri.TaxCalculatedWithMaxWeight" | translate }}</p>
        <p id="calculationTypeCoP" v-if="showMaxWeightInfo === false">{{ "App.Ajoneuvoverolaskuri.TaxCalculatedWithEmissionValue" | translate }}</p>

        <div class="results" v-if="showResults">
            <p>
                {{
                    "App.Ajoneuvoverolaskuri.Results.Pre2026Info" |
                        translate
                }}
            </p>
            <results-table :basicTaxDaily="basicTaxDaily"
                           :yearlyFactor="yearlyFactor"
                           :powerTaxDaily="powerTaxDaily" />
            <p>
                {{
                    "App.Ajoneuvoverolaskuri.Results.Post2025Info" |
                        translate
                }}
            </p>
            <results-table :basicTaxDaily="basicTaxDailyPost2025"
                           :yearlyFactor="yearlyFactorPost2025"
                           :powerTaxDaily="powerTaxDailyPost2025" />
        </div>

        <button type="button" class="primary smallbutton" v-if="cameFromLKPLaskuri === 'true'" v-on:click="returnToLKPKannattavuuslaskuri">{{ "App.Ajoneuvoverolaskuri.ReturnToLKPCalculatorButton" | translate }}</button>
    </div>
</template>

<script lang="ts">
    import { Component, Vue, Prop } from "vue-property-decorator"
    import ListsModel from "../models/listsModel"
    import CalculationFormulas from "../helpers/calculationformulas"
    import Validators from "../helpers/validators"
    import ResultsTable from "./Ajoneuvoverolaskuri/ResultsTable.vue"
    import DatePicker from "./Ajoneuvoverolaskuri/DatePicker.vue"

    @Component({
        components: {
            "results-table": ResultsTable,
            "date-picker": DatePicker
        }
    })
    export default class Ajoneuvoverolaskuri extends Vue {
        mounted(): void {
            this.$nextTick(async () => {
                this.lists = this.$store.getters.ajoneuvoverolaskuriLists
                this.vehicleTypes = this.lists.VehicleTypes.ToStringArray()
                this.selectedVehicle = this.lists.VehicleTypes.car // Default-value
                this.fuelTypes = this.lists.FuelTypes.ToStringArray()
                this.selectedFuel = this.lists.FuelTypes.gasoline // default value
                this.co2measureTypes = this.lists.CO2MeasureTypes.ToStringArray()
                this.selectedCo2measure = this.lists.CO2MeasureTypes.noEmission // Default-value
                this.axelAmounts = this.lists.AxelAmounts.ToStringArray()
                this.selectedAxelAmount = this.lists.AxelAmounts.twoAxel // default value
                this.trailerTypes = this.lists.TrailerTypes.ToStringArray()
                this.selectedTrailerType = this.lists.TrailerTypes.noTrailer // default value
            })
        }

        @Prop(String) cameFromLKPLaskuri!: string

        lists: ListsModel = ListsModel.NewEmptyModel()
        vehicleTypes: Array<string> | undefined = []
        fuelTypes: Array<string> | undefined = []
        co2measureTypes: Array<string> | undefined = []
        axelAmounts: Array<string> | undefined = []
        trailerTypes: Array<string> | undefined = []

        // selected values: v-models
        selectedVehicle: string = ""
        selectedCo2measure: string = ""
        maximumVehicleMass: string = ""
        manufactureDate: string | undefined = undefined
        selectedFuel: string = ""
        co2MeasureValue: string = ""
        selectedAxelAmount: string = ""
        selectedTrailerType: string = ""

        // input visibilities
        showFuelAndCOMeasure: boolean = false
        showCoValue: boolean = false
        showMaxWeightInfo: boolean | null = null
        showResults: boolean = false

        // input validation
        invalidFields: Array<object> = []

        // new tax date
        newTaxDate: number = Date.parse("2021-10-01")

        // results
        basicTaxDaily: number = 0.0
        basicTaxDailyForElectricCar: number = 0.146
        basicTaxDailyForElectricCarPost2025: number = 0.291
        basicTaxDailyForElectricCarAfterNewTaxDate: number = 0.324
        basicTaxDailyForElectricCarAfterNewTaxDatePost2025: number = 0.469
        powerTaxDaily: number = 0.0
        yearlyFactor: number = 365

        basicTaxDailyPost2025: number = 0.0
        powerTaxDailyPost2025: number = 0.0
        yearlyFactorPost2025: number = 365

        get manufactureDateLabel(): string | undefined {
            return this.$t("App.Ajoneuvoverolaskuri.ManufactureDateLabel")
        }

        changeDate(dateString: string | null): void {
            if (dateString) {
                this.manufactureDate = dateString
                this.updateSelections()
            } else {
                this.manufactureDate = undefined
                this.updateSelections()
            }
        }

        updateSelections(): void {
            this.checkFieldsVisibility()
            if (this.checkAllFieldsHaveValidValues()) {
                this.calculateResults()
                this.calculateResultsPost2025()
            } else {
                this.showMaxWeightInfo = null
                this.showResults = false
            }
        }

        checkFieldsVisibility(): void {
            if (this.selectedVehicle === this.lists.VehicleTypes.truck) {
                this.showFuelAndCOMeasure = true
            } else {
                this.showFuelAndCOMeasure = false
            }

            if (this.selectedCo2measure !== this.lists.CO2MeasureTypes.noEmission) {
                this.showCoValue = true
            } else {
                this.showCoValue = false
            }
        }

        checkAllFieldsHaveValidValues(): boolean {
            // tarkista yhteiset kentät
            let success = true
            if (!Validators.validateNumberField(this.maximumVehicleMass)) {
                // eslint-disable-next-line
                this.invalidFields["maximumVehicleMass"] = this.maximumVehicleMass !== "" ? this.$t("App.Ajoneuvoverolaskuri.Fields.NumberFieldInvalid") : ""
                success = false
            } else {
                // eslint-disable-next-line
                this.invalidFields["maximumVehicleMass"] = ""
            }

            // tarkista valintakohtaiset
            if (!this.showFuelAndCOMeasure) {
                // Päivämääräkenttää ei voida validoida duetds komponentin takia
                if (!this.manufactureDate) {
                    // eslint-disable-next-line
                    success = false
                } else {
                    // Jätetty kuitenkin valmis pohja virheilmoituksen näyttöä varten, jos komponentti tulevaisuudessa vaihdetaan.
                    // eslint-disable-next-line
                    this.invalidFields["manufactureDate"] = ""
                }
                if (this.showCoValue) {
                    if (!Validators.validateNumberField(this.co2MeasureValue)) {
                        // eslint-disable-next-line
                        this.invalidFields["co2MeasureValue"] = this.co2MeasureValue !== "" ? this.$t("App.Ajoneuvoverolaskuri.Fields.NumberFieldInvalid") : ""
                        success = false
                    } else {
                        // eslint-disable-next-line
                        this.invalidFields["co2MeasureValue"] = ""
                    }
                }
            }

            return success
        }

        calculateResults(): void {
            // kuorma-auto valittu
            if (this.showFuelAndCOMeasure) {
                this.basicTaxDaily = 0.000
                this.powerTaxDaily = CalculationFormulas.TruckPowerTax(this.lists, this.selectedAxelAmount, this.selectedTrailerType, this.maximumVehicleMass)
                this.showMaxWeightInfo = true
            } else { // joku muu kulkuneuvotyyppi valittu
                // 1. perusvero
                if (this.selectedCo2measure === this.lists.CO2MeasureTypes.noEmission) {
                    // lasketaan kokonaismassan perusteella, jos ei päästöarvoa valittu. Jos käyttövoimana sähkö, käytetään pienintä perusveroa
                    if (this.selectedFuel === this.lists.FuelTypes.electricity) {
                        this.basicTaxDaily = (Date.parse(this.manufactureDate!) >= this.newTaxDate)
                            ? this.basicTaxDailyForElectricCarAfterNewTaxDate
                            : this.basicTaxDailyForElectricCar
                    } else {
                        this.basicTaxDaily = CalculationFormulas.calculateBasicTaxWithMass(parseInt(this.maximumVehicleMass))
                    }
                    this.showMaxWeightInfo = true
                } else {
                    // lasketaan kokonaismassan perusteella, jos ei päästöarvoa valittu. Jos käyttövoimana sähkö, käytetään pienintä perusveroa
                    if (this.selectedFuel === this.lists.FuelTypes.electricity &&
                        parseInt(this.co2MeasureValue) <= 0 &&
                        (this.selectedCo2measure === this.lists.CO2MeasureTypes.nedcEmission || this.selectedCo2measure === this.lists.CO2MeasureTypes.wltpEmission)
                    ) {
                        this.basicTaxDaily = (Date.parse(this.manufactureDate!) >= this.newTaxDate)
                            ? this.basicTaxDailyForElectricCarAfterNewTaxDate
                            : this.basicTaxDailyForElectricCar
                        this.showMaxWeightInfo = true
                    } else {
                        // lasketaan kokonaismassan tai päästöarvon perusteella
                        const taxAndCalculateType = CalculationFormulas.BasicTaxForOtherVehicles(
                            this.lists,
                            this.selectedVehicle,
                            new Date(this.manufactureDate!).getFullYear(),
                            parseInt(this.maximumVehicleMass),
                            this.selectedCo2measure,
                            parseInt(this.co2MeasureValue)
                        )

                        this.basicTaxDaily = taxAndCalculateType.tax
                        this.showMaxWeightInfo = taxAndCalculateType.calculatedWithMass
                    }
                }

                // 2. käyttövoimavero
                if (this.selectedFuel === this.lists.FuelTypes.gasoline ||
                    this.selectedFuel === this.lists.FuelTypes.selfLoadingGasolineHybrid
                ) {
                    // Bensa ja itselataavat bensa-autot, ei käyttövoimaveroa
                    this.powerTaxDaily = 0.000
                } else {
                    if (this.selectedVehicle === this.lists.VehicleTypes.car || this.selectedVehicle === this.lists.VehicleTypes.dualPurposeCar) {
                        // henkilöauto tai kaksikäyttöauto
                        this.powerTaxDaily = CalculationFormulas.PowerTaxForCarAndDualPurposeCar(this.lists, this.selectedFuel, parseInt(this.maximumVehicleMass))
                    } else if (this.selectedVehicle === this.lists.VehicleTypes.van || this.selectedVehicle === this.lists.VehicleTypes.camperVan) {
                        // pakettiauto tai matkailuauto
                        this.powerTaxDaily = Math.ceil((parseInt(this.maximumVehicleMass) / 100)) * CalculationFormulas.powerTaxForVanOrCamperVan / 100
                    }
                }
            }

            this.showResults = true
        }

        calculateResultsPost2025(): void {
            // kuorma-auto valittu
            if (this.showFuelAndCOMeasure) {
                this.basicTaxDailyPost2025 = 0.0
                this.powerTaxDailyPost2025 = CalculationFormulas.TruckPowerTax(
                    this.lists,
                    this.selectedAxelAmount,
                    this.selectedTrailerType,
                    this.maximumVehicleMass
                )
                this.showMaxWeightInfo = true
            } else {
                // joku muu kulkuneuvotyyppi valittu
                // 1. perusvero
                if (
                    this.selectedCo2measure ===
                    this.lists.CO2MeasureTypes.noEmission
                ) {
                    // lasketaan kokonaismassan perusteella, jos ei päästöarvoa valittu. Jos käyttövoimana sähkö, käytetään pienintä perusveroa
                    if (this.selectedFuel === this.lists.FuelTypes.electricity) {
                        this.basicTaxDailyPost2025 = (Date.parse(this.manufactureDate!) >= this.newTaxDate)
                            ? this.basicTaxDailyForElectricCarAfterNewTaxDatePost2025
                            : this.basicTaxDailyForElectricCarPost2025
                    } else {
                        this.basicTaxDailyPost2025 =
                            CalculationFormulas.calculateBasicTaxWithMassPost2025(
                                parseInt(this.maximumVehicleMass)
                            )
                    }
                    this.showMaxWeightInfo = true
                } else {
                    // lasketaan kokonaismassan perusteella, jos ei päästöarvoa valittu. Jos käyttövoimana sähkö, käytetään pienintä perusveroa
                    if (
                        this.selectedFuel === this.lists.FuelTypes.electricity &&
                        parseInt(this.co2MeasureValue) <= 0 &&
                        (this.selectedCo2measure ===
                            this.lists.CO2MeasureTypes.nedcEmission ||
                            this.selectedCo2measure ===
                            this.lists.CO2MeasureTypes.wltpEmission)
                    ) {
                        this.basicTaxDailyPost2025 = (Date.parse(this.manufactureDate!) >= this.newTaxDate)
                            ? this.basicTaxDailyForElectricCarAfterNewTaxDatePost2025
                            : this.basicTaxDailyForElectricCarPost2025
                        this.showMaxWeightInfo = true
                    } else {
                        // lasketaan kokonaismassan tai päästöarvon perusteella
                        const taxAndCalculateType =
                            CalculationFormulas.BasicTaxForOtherVehiclesPost2025(
                                this.lists,
                                this.selectedVehicle,
                                new Date(this.manufactureDate!).getFullYear(),
                                parseInt(this.maximumVehicleMass),
                                this.selectedCo2measure,
                                parseInt(this.co2MeasureValue)
                            )
                        this.basicTaxDailyPost2025 = taxAndCalculateType.tax
                        this.showMaxWeightInfo =
                            taxAndCalculateType.calculatedWithMass
                    }
                }

                // 2. käyttövoimavero
                if (
                    this.selectedFuel === this.lists.FuelTypes.gasoline ||
                    this.selectedFuel ===
                    this.lists.FuelTypes.selfLoadingGasolineHybrid
                ) {
                    // Bensa ja itselataavat bensa-autot, ei käyttövoimaveroa
                    this.powerTaxDailyPost2025 = 0.0
                } else {
                    if (
                        this.selectedVehicle === this.lists.VehicleTypes.car ||
                        this.selectedVehicle ===
                        this.lists.VehicleTypes.dualPurposeCar
                    ) {
                        // henkilöauto tai kaksikäyttöauto
                        this.powerTaxDailyPost2025 =
                            CalculationFormulas.PowerTaxForCarAndDualPurposeCarPost2025(
                                this.lists,
                                this.selectedFuel,
                                parseInt(this.maximumVehicleMass)
                            )
                    } else if (this.selectedVehicle === this.lists.VehicleTypes.camperVan) {
                        // matkailuauto
                        this.powerTaxDailyPost2025 = parseInt(this.maximumVehicleMass) >= 7500
                            ? 4.125
                            : (Math.ceil(parseInt(this.maximumVehicleMass) / 100) * CalculationFormulas.powerTaxForCamperVanPost2025) / 100
                    } else if (this.selectedVehicle === this.lists.VehicleTypes.van) {
                        // pakettiauto
                        this.powerTaxDailyPost2025 = (Math.ceil(parseInt(this.maximumVehicleMass) / 100) * CalculationFormulas.powerTaxForVanOrCamperVan) / 100
                    }
                }
            }

            this.showResults = true
        }

        returnToLKPKannattavuuslaskuri(): void {
            this.$store.dispatch("setBasicAndPowerTax", {
                basicTax: this.basicTaxDaily.toFixed(3),
                powerTax: this.powerTaxDaily.toFixed(3)
            })
            this.$router.go(-1)
        }
    }
</script>

<style scoped>
    .laskuri-block {
        width: 100%;
        max-width: 1000px;
        padding: 20px 40px;
        background-color: rgb(242, 244, 248);
        text-align: left;
        border-top: 7px solid rgb(2, 98, 115);
    }
        .laskuri-block .input-row {
            margin-bottom: 15px;
        }
        .laskuri-block .full-width {
            width: 100%;
        }
            .laskuri-block .full-width select {
                width: 100%
            }
            .laskuri-block div.dropdown:after {
                position: absolute;
                right: 29px;
                top: 12px;
            }

    .invalid-input-message {
        color: red;

    }
</style>
